import React from 'react';

import * as BioUtil from 'biojs/Util';
import {Enzyme} from 'profile/ProfileConstants';
import makeDeprecatedJadeStyleTemplate from 'util/makeDeprecatedJadeStyleTemplate';

interface BaseSlotProps {
  active: boolean;
  slotName: string;
}

interface EmptySlotProps extends BaseSlotProps {
  empty: true;
}

interface NonEmptySlotProps extends BaseSlotProps {
  empty: false;
  enzymes: Array<Enzyme>;
  length: number;
  name: string;
  reverse: boolean;
}

export type SlotProps = EmptySlotProps | NonEmptySlotProps;

function Slot(props: SlotProps): JSX.Element {
  const {active, slotName, empty} = props;
  if (empty) {
    return (
      <>
        <div className="assembly-slot assembly-slot-single">
          <div className="slot-activator step-container">
            <div className="name">{slotName}</div>
          </div>
        </div>
        {active && (
          <div className="assembly-step u-overflow-x-hidden">
            <div className="row">
              <div className="col col-sm-4 preview-container">
                <h4>Preview</h4>
                <div className="preview" />
              </div>
              <div className="col col-lg-6 col-sm-5 text-center">
                <div className="blank-msg">
                  <div className="blank-msg-inner">
                    Shift select two enzymes on the sequence map or run a digest and select a fragment.
                  </div>
                </div>
              </div>
              <div className="col col-lg-2 col-sm-3">
                <div className="buttonGroup pull-right u-small-spacing-right">
                  <div className="blank-msg">
                    <div className="blank-msg-inner">
                      <button type="button" className="button button--mini button--secondary set">
                        Set from Selection
                      </button>
                      <button type="button" className="button button--mini button--tertiaryGray js-delete">
                        <span className="glyphicon glyphicon-trash" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  } else {
    const {enzymes, length, name, reverse} = props;
    return (
      <>
        <div className="assembly-slot assembly-slot-single">
          <div className="slot-activator step-container sequence-set">
            <div className="name">{name}</div>
            {reverse ? (
              <div className="supplementary">
                {BioUtil.formatBaseCount(length)} &sdot;{' '}
                <span style={{color: enzymes[1].color}}>{enzymes[1].name}</span>,{' '}
                <span style={{color: enzymes[0].color}}>{enzymes[0].name}</span>
              </div>
            ) : (
              <div className="supplementary">
                {BioUtil.formatBaseCount(length)} &sdot;{' '}
                <span style={{color: enzymes[0].color}}>{enzymes[0].name}</span>,{' '}
                <span style={{color: enzymes[1].color}}>{enzymes[1].name}</span>
              </div>
            )}
          </div>
        </div>
        {active && (
          <div className="assembly-step sequence-set u-overflow-x-hidden">
            <div className="row">
              <div className="col col-sm-5 preview-container">
                <h4>Preview</h4>
                <div className="preview" />
              </div>
              <div className="col col-sm-4 assembly-check-mount" />
              <div className="col col-sm-3 step-controls u-lr-split">
                <div className="links">
                  <div>
                    <a className="js-flip">Reverse Orientation</a>
                  </div>
                  <div>
                    <a className="js-jump">Jump to Selection</a>
                  </div>
                  <div className="buffers" />
                </div>
                <div className="buttonGroup buttonGroup--vertical step-options clear-offset">
                  <button
                    type="button"
                    data-toggle="tooltip"
                    data-placement="left"
                    title="Done"
                    className="button button--small button--primary js-done"
                  >
                    <span className="fa fa-check" />
                  </button>
                  <button
                    type="button"
                    data-toggle="tooltip"
                    data-placement="left"
                    title="Reset"
                    className="button button--small button--tertiaryGray js-reset"
                  >
                    <span className="glyphicon glyphicon-ban-circle" />
                  </button>
                  <button
                    type="button"
                    data-toggle="tooltip"
                    data-placement="left"
                    title="Delete"
                    className="button button--small button--tertiaryGray js-delete"
                  >
                    <span className="glyphicon glyphicon-trash" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}

export default makeDeprecatedJadeStyleTemplate<SlotProps>(Slot);
